import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, TrendingUp, Shield, Award, DollarSign, Users } from "lucide-react";

const Index = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">XM Trading</span>
            </div>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white">
              Open Account
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-6 bg-orange-100 text-orange-800 hover:bg-orange-100">
            Exclusive Partner Offer
          </Badge>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            XM Partner Code PY8GQ - $30 No-Deposit Bonus
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Start your trading journey with XM's exclusive partner code PY8GQ and receive a $30 no-deposit bonus. 
            Join millions of traders worldwide on the award-winning XM platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3">
              Claim $30 Bonus Now
            </Button>
            <Button size="lg" variant="outline" className="px-8 py-3">
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Partner Code Highlight */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-r from-orange-500 to-red-500 text-white border-0">
            <CardContent className="p-8 text-center">
              <DollarSign className="h-16 w-16 mx-auto mb-4" />
              <h2 className="text-3xl font-bold mb-4">Partner Code: PY8GQ</h2>
              <p className="text-xl mb-6">Use this exclusive code to unlock your $30 no-deposit bonus</p>
              <div className="bg-white/20 rounded-lg p-4 inline-block">
                <span className="text-2xl font-mono font-bold tracking-wider">PY8GQ</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose XM with Partner Code PY8GQ?</h2>
            <p className="text-lg text-gray-600">Exclusive benefits and world-class trading experience</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-2 hover:border-orange-200 transition-colors">
              <CardHeader>
                <DollarSign className="h-12 w-12 text-orange-500 mb-2" />
                <CardTitle>$30 No-Deposit Bonus</CardTitle>
                <CardDescription>
                  Start trading immediately with our exclusive $30 bonus - no deposit required
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:border-orange-200 transition-colors">
              <CardHeader>
                <Shield className="h-12 w-12 text-blue-500 mb-2" />
                <CardTitle>Regulated & Secure</CardTitle>
                <CardDescription>
                  Licensed by top-tier regulators including CySEC, ASIC, and FCA for maximum security
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:border-orange-200 transition-colors">
              <CardHeader>
                <Award className="h-12 w-12 text-green-500 mb-2" />
                <CardTitle>Award-Winning Platform</CardTitle>
                <CardDescription>
                  Trade on MetaTrader 4 & 5 with advanced tools and lightning-fast execution
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:border-orange-200 transition-colors">
              <CardHeader>
                <TrendingUp className="h-12 w-12 text-purple-500 mb-2" />
                <CardTitle>1000+ Instruments</CardTitle>
                <CardDescription>
                  Access forex, stocks, commodities, indices, and cryptocurrencies all in one place
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:border-orange-200 transition-colors">
              <CardHeader>
                <Users className="h-12 w-12 text-indigo-500 mb-2" />
                <CardTitle>24/7 Support</CardTitle>
                <CardDescription>
                  Get expert support in 30+ languages whenever you need assistance
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:border-orange-200 transition-colors">
              <CardHeader>
                <CheckCircle className="h-12 w-12 text-teal-500 mb-2" />
                <CardTitle>No Hidden Fees</CardTitle>
                <CardDescription>
                  Transparent pricing with no hidden charges or commissions on most accounts
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* How to Use Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How to Use Partner Code PY8GQ</h2>
            <p className="text-lg text-gray-600">Simple steps to claim your $30 bonus</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-orange-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-semibold mb-2">Register Account</h3>
              <p className="text-gray-600">Click "Open Account" and fill in your registration details</p>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-semibold mb-2">Enter Partner Code</h3>
              <p className="text-gray-600">Use partner code <strong>PY8GQ</strong> during registration</p>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-semibold mb-2">Start Trading</h3>
              <p className="text-gray-600">Verify your account and receive your $30 bonus instantly</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-500 to-red-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Start Trading with $30 Bonus?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Join over 5 million traders worldwide and experience the XM advantage
          </p>
          <Button size="lg" className="bg-white text-orange-500 hover:bg-gray-100 px-8 py-3 text-lg">
            Open Free Account with PY8GQ
          </Button>
          <p className="text-sm text-orange-100 mt-4">
            *Terms and conditions apply. Bonus subject to verification.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <TrendingUp className="h-6 w-6" />
                <span className="text-xl font-bold">XM Trading</span>
              </div>
              <p className="text-gray-400 text-sm">
                Award-winning online trading platform serving millions of clients worldwide.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Trading</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>Forex</li>
                <li>Stocks</li>
                <li>Commodities</li>
                <li>Indices</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Platforms</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>MetaTrader 4</li>
                <li>MetaTrader 5</li>
                <li>XM WebTrader</li>
                <li>Mobile Apps</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>24/7 Live Chat</li>
                <li>Email Support</li>
                <li>Phone Support</li>
                <li>FAQ</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2024 XM Trading. All rights reserved. Partner Code: PY8GQ</p>
            <p className="mt-2">
              Risk Warning: Trading involves substantial risk and may result in the loss of your invested capital.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
